import { type Brand } from '@curology/ui-components-web/brand';

import { type ExpressCurologySignUpSkinPdpSourceConfigType } from '@/components/flow/expressCurologySignUpSkin/types';
import {
  getNextBoxTotals,
  getTotal,
  getTotalWithoutDiscount,
} from '@/components/flow/expressCurologySignUpSkin/utils';
import { PAYMENT_TYPES, PROCESSOR_NAMES } from '@/consts/payments';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { getPaymentErrorMessage } from '@/helpers/productSelectionAndCheckout/getPaymentErrorMessage';
import { reportUnhandledPaymentErrorToSentry } from '@/helpers/productSelectionAndCheckout/reportUnhandledPaymentErrorToSentry';
import { updateRegistrationEligibility } from '@/services/eligibilityService/actions';
import { ApplePayService } from '@/services/productSelectionAndCheckoutService/applePay';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { editSessionBag } from '@/services/visitsService/client';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

export const applePayButtonStyleConfig: {
  borderRadius: string;
  height: string;
  theme: 'dark' | 'light' | 'light-outline';
  type: 'default' | 'donate' | 'buy';
} = {
  borderRadius: '26px',
  height: '52px',
  theme: 'dark',
  type: 'default',
};

const systemErrorMessage = 'A system error occurred. Please try again.';

export const getPaymentRequestOptions = (
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType,
  selectedAddOnProductItemIds: number[],
) => {
  const { shippingPrice } = bundleConfig;

  const { nextBoxTotal } = getNextBoxTotals(
    bundleConfig,
    selectedAddOnProductItemIds,
  );

  const total = getTotal(bundleConfig, selectedAddOnProductItemIds);

  const totalWithoutDiscount = getTotalWithoutDiscount(
    bundleConfig,
    selectedAddOnProductItemIds,
  );

  const country = 'US';
  const currency = 'usd';

  const displayItems = [
    {
      amount: totalWithoutDiscount,
      label: '30-day trial box',
    },
    {
      amount: (totalWithoutDiscount - shippingPrice) * -1,
      label: 'Discount',
    },
    {
      amount: shippingPrice,
      label: 'Shipping',
    },
    {
      amount: nextBoxTotal,
      label: 'Your next box',
    },
  ];

  const requestPayerEmail = false;
  const requestPayerName = true;
  const requestPayerPhone = true;
  const requestShipping = true;

  const shippingOptions = [
    {
      amount: shippingPrice,
      id: 'basic',
      label: 'Ground Shipping',
    },
  ];

  return {
    country,
    currency,
    displayItems,
    requestPayerEmail,
    requestPayerName,
    requestPayerPhone,
    requestShipping,
    shippingOptions,
    total: {
      amount: total,
      label: 'Curology, inc.',
    },
  };
};

const getActivateExpressCheckoutTrialData = (
  event: any,
  enableForms: () => void,
  flashMessages: any,
) => {
  let firstname = '';
  let lastname = '';

  const payerFullName = event.payerName;

  if (payerFullName) {
    const [payerFirstName, ...payerSecondNames] = payerFullName.split(' ');

    firstname = payerFirstName.trim();
    lastname = payerSecondNames.join(' ').trim();
  }

  if (firstname === '' || lastname === '') {
    enableForms();

    event.complete('fail');

    flashMessages.error({
      title: 'Sorry!',
      message:
        'We cannot proceed because your first name and last name are not set in Apple Pay.',
    });

    return;
  }

  const {
    payerPhone: phone = '',
    shippingAddress: {
      addressLine = '',
      city = '',
      postalCode: zipcode = '',
      region: state = '',
    } = {},
    token,
  } = event;

  const method = PAYMENT_TYPES.APPLE_PAY;
  const vendor = PROCESSOR_NAMES.STRIPE;

  return {
    address1: addressLine?.[0],
    address2: addressLine?.[1],
    city,
    firstname,
    lastname,
    method,
    paymentNonce: token.id,
    ...(token.card
      ? {
          cardLastFour: token.card?.last4,
          cardFundingType: token.card?.funding,
        }
      : {}),
    phone,
    state,
    vendor,
    zipcode,
  };
};

const completeApplePayWithError = (
  event: any,
  enableForms: () => void,
  message: string,
  flashMessages: any,
) => {
  enableForms();
  flashMessages.error({ title: 'Sorry!', message });
  event.complete('fail');
  trackEvent(AnalyticsEvent.APPLE_PAY_FAILED, { flow: FlowTypes.PDP_CHECKOUT });
};

const activateTrialAndComplete = async (
  event: any,
  brand: Brand,
  data: ReturnType<typeof getActivateExpressCheckoutTrialData>,
  treatmentCategory: TreatmentCategory,
  uuid: string,
  flashMessages: any,
  onNext: () => void,
) => {
  if (!data) return false;

  await updateRegistrationEligibility(brand, {
    state: data.state,
    treatmentCategories: treatmentCategory,
  });

  await ApplePayService.activateExpressCheckoutTrial({ brand, data });

  await editSessionBag({
    brand,
    sessionBag: {
      hasActivatedTrial: true,
      hasAddedPaymentMethod: true,
      hasAddedShippingAddress: true,
    },
    uuid,
  });

  flashMessages.success({
    title: 'Order confirmed!',
    message:
      'Now answer some quick questions to help your provider personalize your formula.',
  });

  event.complete('success');
  trackEvent(AnalyticsEvent.APPLE_PAY_SUCCEEDED, {
    flow: FlowTypes.PDP_CHECKOUT,
  });
  trackEvent(AnalyticsEvent.CARD_SAVED, {
    card_type: event.token.card?.funding || '',
    flow: FlowTypes.PDP_CHECKOUT,
  });
  onNext();
  return true;
};

export const handlePaymentRequestButtonOnToken = async (
  event: any,
  brand: Brand,
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType,
  disableForms: () => void,
  enableForms: () => void,
  isSubmitting: boolean,
  selectedAddOnProductItemIds: number[],
  treatmentCategory: TreatmentCategory,
  uuid: string,
  flashMessages: any,
  onNext: () => void,
  handleIfSessionExpired: (status: number | undefined) => boolean,
) => {
  if (!isSubmitting) {
    if (event.shippingAddress) {
      try {
        disableForms();

        const selectedItemIds = [
          ...bundleConfig.setProducts
            .map((setProduct) => setProduct.itemIds)
            .flat(),
          ...selectedAddOnProductItemIds,
        ];

        await ProductSelectionAndCheckoutService.updateSelectedItems(
          brand,
          selectedItemIds,
          treatmentCategory,
        );

        const data = getActivateExpressCheckoutTrialData(
          event,
          enableForms,
          flashMessages,
        );

        const success = await activateTrialAndComplete(
          event,
          brand,
          data,
          treatmentCategory,
          uuid,
          flashMessages,
          onNext,
        );
        if (!success) {
          completeApplePayWithError(
            event,
            enableForms,
            systemErrorMessage,
            flashMessages,
          );
        }
      } catch (e: any) {
        if (handleIfSessionExpired(e.response?.status)) {
          enableForms();
          event.complete('fail');
          return;
        }

        reportUnhandledPaymentErrorToSentry(e);
        const message = getPaymentErrorMessage(
          e.response?.data,
          systemErrorMessage,
        );
        completeApplePayWithError(event, enableForms, message, flashMessages);
      }
    } else {
      completeApplePayWithError(
        event,
        enableForms,
        'We cannot proceed because a shipping address is not set in Apple Pay.',
        flashMessages,
      );
    }
  }
};
